import React, { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import "./Jewellery.css";
import Filter from "../../components/Common/Filter";
import Card from "../../components/Common/Card";
import QuickViewDialog from "../../components/Common/QuickViewDialog";
import AuthContext from '../../context/AuthContext';
import { StyleSearch } from "../../api/styleJewelleryService";
import { useFilters } from "../../context/JWFilterContext";
import { useAuth } from "../../context/AuthContext";

const Jewellery = () => {
    document.title = "Style Inventory | Meera Jewellery House Admin";

    const { user, showToast } = useAuth();
    const { user: { LEDGER_ID: ledgerId } } = useContext(AuthContext);
    const [ringData, setRingData] = useState([]);
    const [productCount, setProductCount] = useState(0);
    const [productType, setProductType] = useState("Jewellery");
    const [sortValue, setSortValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [pageSize, setPageSize] = useState(50);
    const [pageNo, setPageNo] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingData, setLoadingData] = useState(true);
    const [totalPage, setTotalPage] = useState(1);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const tag = searchParams.get("Tag") || "";
    const { filters, setSearch } = useFilters();
    const requestInProgress = useRef(false);
    const filterChanged = useRef(false);

    useEffect(() => {
        if (tag) {
            setSearch(tag);
        }
    }, [tag]);

    useEffect(() => {
        setPageNo(1);
        fetchJewelleryData(1);
    }, [filters, sortValue]);

    const handleLoadMore = () => {
        const nextPage = pageNo + 1;
        if (nextPage <= totalPage) {
            setPageNo(nextPage);
            fetchJewelleryData(nextPage);
        }
    };

    // #region SMART SEARCH
    const searchbyTag = async (page = 1) => {

    };

    // #endregion SMART SEARCH

    // #region JEWELLERY SEARCH AND PAGINATION
    const fetchJewelleryData = async (page = 1) => {

        if (requestInProgress.current) return;

        if (Number(page) > totalPage) {
            return;
        }

        requestInProgress.current = true;
        setIsLoading(true);
        setLoadingData(true);

        try {
            const requestParams = {
                Party_ID: user?.SelectedCustomer_ID || ledgerId || "0",
                Category: filters.styleNames.length ? filters.styleNames.join("~") : "",
                SubCategory: filters.subCategoryNames.length ? filters.subCategoryNames.join("~") : "",
                Metal: filters.metalToneNames.length ? filters.metalToneNames.join("~") : "",
                GoldQuality: filters.goldQualityNames.length ? filters.goldQualityNames.join("~") : "",
                Collection: filters.collectionNames.length ? filters.collectionNames.join("~") : "",
                StyleNo: filters.searchText?.trim() || "",
                ItemSize: filters.itemSizeNames.length ? filters.itemSizeNames.join("~") : "",
                FromPrice: filters.priceRange[0]?.toString() ?? "",
                ToPrice: filters.priceRange[1]?.toString() ?? "",
                FromCarat: filters.caratRange[0]?.toString() ?? "",
                ToCarat: filters.caratRange[1]?.toString() ?? "",
                FromGoldWeight: filters.goldWtRange[0]?.toString() ?? "",
                ToGoldWeight: filters.goldWtRange[1]?.toString() ?? "",
                PageNo: String(page),
                PageSize: String(pageSize),
                SortBy: sortValue || ""
            };

            const response = await StyleSearch(requestParams);
            const message = response?.Message?.[0];

            if (response?.Products && response.Products.length > 0) {
                if (page === 1) {
                    setRingData(response.Products);
                } else {
                    setRingData((prev) => [...prev, ...response.Products]);
                }
                setHasMore(response.Products.length >= pageSize);
                setProductCount(Number(response?.Pagination?.[0]?.Total_Record) || 0);
                setTotalPage(response?.Pagination?.[0]?.Total_Page);
            }
            else {
                if (page === 1) {
                    setRingData([]);
                    setProductCount(0);
                }
            }
        } catch (error) {
            console.error("Error fetching jewellery data:", error);
        } finally {
            requestInProgress.current = false;
            setIsLoading(false);
            setLoadingData(false);
        }
    };

    // #endregion JEWELLERY SEARCH AND PAGINATION

    // #region UPDATE JEWELLERY DATA AFTER WISHLIST FUNCTIONALITY
    const handleUpdateWishlistBasketID = (Style_ID, Wishlist_ID) => {
        setRingData(prevRings => {
            const updatedRings = prevRings.map(ring => {
                if (ring.Style_ID === Style_ID) {
                    return {
                        ...ring,
                        Wishlist_ID: Wishlist_ID,
                        IS_AddedInWishlist: Wishlist_ID ? "YES" : "NO",
                    };
                }
                return ring;
            })
            return updatedRings;
        });
    };
    // #endregion UPDATE JEWELLERY DATA AFTER WISHLIST FUNCTIONALITY

    // #region HANDLE RECENT VIEW UPDATE (to sync after wishlist)
    const handleRecentViewUpdate = (Style_ID, newBasketID) => {
        setRingData((prevRings) =>
            prevRings.map((ring) =>
                ring.Style_ID === Style_ID
                    ? {
                        ...ring,
                        WishlistBasket_ID: newBasketID,
                        IsWishlist: newBasketID ? 1 : 0,
                    }
                    : ring
            )
        );
    };

    // #endregion HANDLE RECENT VIEW UPDATE (to sync after wishlist)

    const handleOpenQuickView = (product) => {
        setSelectedProduct(product);
        setIsQuickViewOpen(true);
    };

    const handleCloseQuickView = () => {
        setSelectedProduct(null);
        setIsQuickViewOpen(false);
    };

    return (
        <div>
            <div className="filter_div">
                <Filter
                    productType={productType}
                    setProductType={setProductType}
                    setSelectedSortValue={setSortValue}
                    loadedCount={ringData.length}
                    totalCount={productCount}
                />
            </div>
            <div className="card_div">
                <Card
                    ringData={ringData}
                    hasMore={hasMore}
                    isLoading={loadingData}
                    showColorPalate={true}
                    onRecentViewUpdate={handleRecentViewUpdate}
                    onLoadMore={handleLoadMore}
                    fetchData={fetchJewelleryData}
                    onUpdateWishlist={handleUpdateWishlistBasketID}
                    showCartIcon={false}
                    onQuickViewOpen={handleOpenQuickView}
                />
            </div>
            <QuickViewDialog 
                visible={isQuickViewOpen} 
                onHide={handleCloseQuickView} 
                product={selectedProduct} 
                detailLinkPrefix="/detail?style_ID=" 
            />
        </div>
    );
};

export default Jewellery;
